import React, { useState, useEffect } from 'react';
import { setPageMeta } from '../lib/seo';

function Docs() {
  const [activeSection, setActiveSection] = useState('overview');
  const [activeSubsection, setActiveSubsection] = useState('about-service');
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
    setPageMeta({
      title: 'Documentation — Elorimi',
      description: 'Guides and instructions for connecting to your Elorimi Cloud PC or Cloud VPS.',
    });
  }, []);

  const sections = {
    "overview": {
      title: "Overview",
      subsections: ["about-service"]
    },
    "cloudpc": {
      title: "Connect to Cloud PC",
      subsections: ["rustdesk", "installrustdesk", "connectrustdesk", "optimizerustdesk", "recommendedinternetrustdesk", "securityrustdesk"]
    },
    "cloudvps": {
      title: "Connect to Cloud VPS",
      subsections: ["connection-details", "connectlinuxvps", "connectwindowsvps", "optimizevps", "recommendedinternetvps", "securityvps"]
    },
    "support": {
      title: "Contact Us",
      subsections: ["support"]
    }
  };

  // Flatten all subsections into a single array for navigation
  const allSubsections = Object.keys(sections).reduce((acc, section) => {
    sections[section].subsections.forEach(sub => {
      acc.push({ key: sub, section });
    });
    return acc;
  }, []);

  // Get current index in the flat array
  const currentIndex = allSubsections.findIndex(item => item.key === activeSubsection);

  // Get previous and next navigation items
  const prevItem = currentIndex > 0 ? allSubsections[currentIndex - 1] : null;
  const nextItem = currentIndex < allSubsections.length - 1 ? allSubsections[currentIndex + 1] : null;

  // Navigation handlers
  const handlePrev = () => {
    if (prevItem) {
      setActiveSection(prevItem.section);
      setActiveSubsection(prevItem.key);
    }
  };

  const handleNext = () => {
    if (nextItem) {
      setActiveSection(nextItem.section);
      setActiveSubsection(nextItem.key);
    }
  };

  const content = {
    'support': {
      title: 'Support',
      content: [
        {
          type: 'text',
          value: 'We are committed to providing reliable and responsive support to ensure your experience remains smooth from start to finish.'
        },
          {
          type: 'link',
          value: {
            text: 'info@elorimi.com',
            url: 'mailto:info@elorimi.com'
          }
        }
      ],
      code: null
    },
    'about-service': {
      title: 'About the Service',
      content: [
        {
          type: 'text',
          value: 'Welcome to our cloud platform — a fast, secure, and flexible environment designed to provide instant access to high-performance Cloud PCs and Virtual Private Servers (VPS).'
        }
      ],
      code: null
    },
 'rustdesk': {
      title: 'Download RustDesk',
      content: [
        {
          type: 'text',
          value: 'To get started, download the RustDesk client for your device from the official website:'
        },
          {
          type: 'link',
          value: {
            text: 'Official RustDesk Website',
            url: 'https://rustdesk.com'
          }
        },
         {
          type: 'text',
          value: 'RustDesk is available for all major platforms: Windows, macOS, Linux, Android and IOS.'
        },
         {
          type: 'text',
          value: 'Make sure you download the latest stable version to ensure the best performance and security.'
        }
      ],
      code: null
    },
    'installrustdesk': {
      title: 'Install the Application',
      content: [
        {
          type: 'text',
          value: 'After downloading, install RustDesk as you would any other application:'
        },
          {
          type: 'list',
          ordered: true,
          value: [
            'Open the downloaded installer file.',
            'Follow the on-screen instructions to complete the installation.',
            'Once installed, launch RustDesk from your applications menu or desktop shortcut.'
          ]
        },
         {
          type: 'text',
          value: 'No special configuration is required at this stage.'
        }
      ],
      code: null
    },
    'connectrustdesk': {
      title: 'Connect to Your Cloud PC',
      content: [
        {
          type: 'text',
          value: 'After installation, you will receive an email or message from our support team containing your personal access details:'
        },
         {
          type: 'list',
          value: [
            'Desk ID — unique identifier of your Cloud PC',
            'Password — secure one-time or permanent password'
          ]
        },
         {
          type: 'text',
          value: 'You will receive these connection details 10 minutes before your scheduled start time.'
        },
         {
          type: 'text',
          value: 'The connection will become active exactly at the chosen start time and will automatically stop and disconnect at the end time you selected during purchase.'
        },
         {
          type: 'text',
          value: 'To connect:'
        },
          {
          type: 'list',
          ordered: true,
          value: [
            'Open RustDesk.',
            'In the “Remote ID” field, enter your Desk ID.',
            'Click Connect.',
            'When prompted, enter your Password.',
            'Wait a few seconds — your Cloud PC desktop will appear on your screen.',
          ]
        },
         {
          type: 'text',
          value: 'You can now use your Cloud PC as if you were sitting in front of it.'
        }
      ],
      code: null
    },
     'optimizerustdesk': {
      title: 'Optimize Your Experience',
      content: [
         {
          type: 'text',
          value: 'For the best performance:'
        },
         {
          type: 'list',
          value: [
            'Use a wired connection or a stable Wi‑Fi network.',
            'Close unnecessary background applications on your local device.',
            'Adjust RustDesk’s quality settings if you experience lag (lower resolution or frame rate).'
          ]
        }
      ],
      code: null
    }, 'recommendedinternetrustdesk': {
      title: 'Recommended Internet Speed',
      content: [
         {
          type: 'text',
          value: 'To ensure smooth operation:'
        },
         {
          type: 'list',
          value: [
            'Minimum: 20 Mbps download / 10 Mbps upload',
            'Recommended: 50 Mbps or higher for HD streaming and design work'
          ]
        },
            {
          type: 'text',
          value: 'A faster connection will provide a more responsive and stable remote experience.'
        }
      ],
      code: null
    },
    'securityrustdesk': {
      title: 'Security Notice',
      content: [
         {
          type: 'text',
          value: 'Your Cloud PC is protected by encrypted connections through RustDesk.'
        },
          {
          type: 'text',
          value: 'However, please follow these important security guidelines:'
        },
         {
          type: 'list',
          value: [
            'Never share your Desk ID or Password with anyone.',
            'Change your password immediately if you suspect unauthorized access.',
            'Always disconnect when you finish your session.',
            'Keep your RustDesk client updated to the latest version.'
          ]
        },
            {
          type: 'text',
          value: 'Following these steps will help keep your Cloud PC secure and ensure a smooth, reliable connection every time.'
        }
      ],
      code: null
    },
       'connection-details': {
      title: 'Prepare Your Connection Details',
      content: [
          {
          type: 'text',
          value: 'Before your session begins, you will receive an email from our support team containing:'
        },
         {
          type: 'list',
          value: [
            'IP Address — the public address of your server',
            'Username — your administrator or user login',
            'Password — a secure temporary or permanent password',
            'Connection Type — SSH or RDP'
          ]
        },
            {
          type: 'text',
          value: 'You will receive these credentials approximately 10 minutes before your scheduled start time.'
        },
          {
          type: 'text',
          value: 'Access will automatically activate at the start of your booking and will terminate once your prepaid session ends.'
        }
      ],
      code: null
    },
       'connectlinuxvps': {
      title: 'Connect to a Linux VPS (SSH)',
      content: [
         {
          type: 'text',
          value: 'If you selected a Linux server, you will connect using SSH (Secure Shell).'
        },
          {
          type: 'text',
          value: 'For macOS / Linux Users'
        },
         {
          type: 'list',
          ordered: true,
          value: [
            'Open the Terminal application.',
            'Enter the following command: ssh <username>@<IP_ADDRESS>',
            'Press Enter.',
            'When prompted, type your password and press Enter again.'
          ]
        },
            {
          type: 'text',
          value: 'You are now connected to your VPS.'
        },
           {
          type: 'text',
          value: 'For Windows Users'
        },
           {
          type: 'text',
          value: 'If you are using Windows, install an SSH client such as PuTTY or use Windows Terminal.'
        },
         {
          type: 'list',
          ordered: true,
          value: [
            'Open your SSH client.',
            'Enter the IP address provided.',
            'Log in using your username and password.'
          ]
        },
            {
          type: 'text',
          value: 'After authentication, your server console will be ready to use.'
        }
      ],
      code: null
    },
       'connectwindowsvps': {
      title: 'Connect to a Windows VPS (RDP)',
      content: [
         {
          type: 'text',
          value: 'If your VPS runs Windows, you will connect via Remote Desktop Protocol (RDP).'
        },
         {
          type: 'list',
          ordered: true,
          value: [
            'Open Remote Desktop Connection on your device: Windows: Search for Remote Desktop Connection; macOS / iOS / Android: Download Microsoft Remote Desktop',
            'Enter your IP Address.',
            'Click Connect.',
            'When prompted, enter your Username and Password.',
            'Accept the security prompt if it appears.'
          ]
        },
            {
          type: 'text',
          value: 'Within a few seconds, your Windows desktop will load.'
        }
      ],
      code: null
    },
       'optimizevps': {
      title: 'Optimize Your Experience',
      content: [
         {
          type: 'text',
          value: 'For the best performance:'
        },
         {
          type: 'list',
          value: [
            'Use a stable internet connection (wired is recommended).',
            'Avoid heavy downloads on your local network during your session.',
            'Close unnecessary applications to reduce latency.',
            'For RDP users, lowering display resolution can improve responsiveness on slower connections.'
          ]
        }
      ],
      code: null
    },
       'recommendedinternetvps': {
      title: 'Recommended Internet Speed',
      content: [
         {
          type: 'text',
          value: 'To maintain a stable remote connection:'
        },
         {
          type: 'list',
          value: [
            'Minimum: 10–20 Mbps',
            'Recommended: 50 Mbps or higher'
          ]
        },
            {
          type: 'text',
          value: 'Lower speeds may still work but can introduce latency.'
        }
      ],
      code: null
    },
       'securityvps': {
      title: 'Security Notice',
      content: [
         {
          type: 'text',
          value: 'Your Cloud VPS is isolated and protected, but please follow these best practices:'
        },
         {
          type: 'list',
          value: [
            'Never share your login credentials.',
            'Change your password if you suspect unauthorized access.',
            'Always log out when your work is complete.',
            'Store your connection details securely.'
          ]
        },
            {
          type: 'text',
          value: 'These measures help ensure your server remains safe and fully under your control.'
        }
      ],
      code: null
    }







  };

  return (
    <div className={isLoaded ? 'loaded' : ''}>
      {/* Animated Background */}
      <div className="bg-animation">
        <div className="bg-mesh"></div>
        <div className="bg-grid"></div>
        <div className="bg-glow bg-glow-1"></div>
        <div className="bg-glow bg-glow-2"></div>
        <div className="bg-glow bg-glow-3"></div>
      </div>

      {/* Docs Section */}
      <section className="docs-section">
        <div className="container">
          <div className="row">
            {/* Sidebar */}
            <div className="col-lg-3">
              <div className="docs-sidebar glass">
                <div className="docs-sidebar-header">
                  <i className="fas fa-book-open"></i>
                  <span>Documentation</span>
                </div>
                
                <nav className="docs-nav">
                  {Object.keys(sections).map(sectionKey => (
                    <div key={sectionKey} className="docs-nav-section">
                      <button
                        className={`docs-nav-section-title ${activeSection === sectionKey ? 'active' : ''}`}
                        onClick={() => {
                          setActiveSection(sectionKey);
                          setActiveSubsection(sections[sectionKey].subsections[0]);
                        }}
                      >
                        <span className="section-icon">{sections[sectionKey].icon}</span>
                        <span>{sections[sectionKey].title}</span>
                        <i className={`fas fa-chevron-down section-arrow ${activeSection === sectionKey ? 'rotated' : ''}`}></i>
                      </button>
                      
                      {activeSection === sectionKey && (
                        <ul className="docs-nav-subsection">
                          {sections[sectionKey].subsections.map(subKey => (
                            <li key={subKey}>
                              <button
                                className={`docs-nav-link ${activeSubsection === subKey ? 'active' : ''}`}
                                onClick={() => {
                                  setActiveSection(sectionKey);
                                  setActiveSubsection(subKey);
                                }}
                              >
                                <span className="link-dot"></span>
                                {content[subKey]?.title}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </nav>
              </div>
            </div>

            {/* Content */}
            <div className="col-lg-9">
              <div className="docs-content glass">
                <div className="docs-content-header">
                  <span className="docs-breadcrumb">
                    Docs / {sections[activeSection].title} / {content[activeSubsection]?.title}
                  </span>
                  <h1 className="docs-title">{content[activeSubsection]?.title}</h1>
                </div>

                <div className="docs-body">
                  {Array.isArray(content[activeSubsection]?.content) ? (
                    content[activeSubsection].content.map((block, i) => {
                      if (block.type === 'text') {
                        return (
                          <p className="docs-text" key={`text-${i}`}>
                            {String(block.value || '').split('\n\n').map((paragraph, idx, arr) => (
                              <span key={idx}>
                                {paragraph}
                                {idx < arr.length - 1 && <><br /><br /></>}
                              </span>
                            ))}
                          </p>
                        );
                      }
                      if (block.type === 'list') {
                        const items = Array.isArray(block.value) ? block.value : [];
                        const isOrdered = Boolean(block.ordered || block.style === 'ordered');
                        const ListTag = isOrdered ? 'ol' : 'ul';
                        return (
                          <ListTag className="docs-list" key={`list-${i}`}>
                            {items.map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ListTag>
                        );
                      }
                      if (block.type === 'image') {
                        const imageValue = typeof block.value === 'string'
                          ? { src: block.value }
                          : block.value;
                        return (
                          <div className="docs-image" key={`image-${i}`}>
                            <img
                              src={imageValue?.src}
                              alt={imageValue?.alt || content[activeSubsection]?.title}
                              style={{
                                maxWidth: '100%',
                                height: 'auto',
                                borderRadius: 'var(--radius-lg)',
                                margin: imageValue?.caption ? '0 0 0.5rem 0' : '0'
                              }}
                            />
                            {imageValue?.caption && (
                              <p className="docs-image-caption">{imageValue.caption}</p>
                            )}
                          </div>
                        );
                      }
                      if (block.type === 'link') {
                        const linkValue = typeof block.value === 'string'
                          ? { text: block.value, url: block.value }
                          : block.value;
                        if (!linkValue?.url) return null;
                        return (
                          <p className="docs-text" key={`link-${i}`}>
                            <a
                              className="docs-link"
                              href={linkValue.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {linkValue.text || linkValue.url}
                            </a>
                          </p>
                        );
                      }
                      return null;
                    })
                  ) : (
                    <p className="docs-text">
                      {content[activeSubsection]?.content.split('\n\n').map((paragraph, i, arr) => (
                        <span key={i}>
                          {paragraph}
                          {i < arr.length - 1 && <><br /><br /></>}
                        </span>
                      ))}
                    </p>
                  )}

                  {content[activeSubsection]?.image && (
                    <div className="docs-image">
                      <img 
                        src={content[activeSubsection].image.src} 
                        alt={content[activeSubsection].image.alt || content[activeSubsection].title}
                        style={{ 
                          maxWidth: '100%', 
                          height: 'auto',
                          borderRadius: 'var(--radius-lg)',
                          margin: content[activeSubsection].image.caption ? '0 0 0.5rem 0' : '0'
                        }}
                      />
                      {content[activeSubsection].image.caption && (
                        <p className="docs-image-caption">{content[activeSubsection].image.caption}</p>
                      )}
                    </div>
                  )}

                  {content[activeSubsection]?.code && (
                    <div className="docs-code-block">
                      <div className="code-header">
                        <span className="code-lang">
                          <i className="fas fa-code"></i>
                          Code Example
                        </span>
                        <button className="code-copy-btn">
                          <i className="fas fa-copy"></i>
                          Copy
                        </button>
                      </div>
                      <pre className="code-content">
                        <code>{content[activeSubsection]?.code}</code>
                      </pre>
                    </div>
                  )}

                  <div className="docs-footer">
                    <div className="docs-footer-nav">
                      <button 
                        className={`docs-footer-btn prev ${!prevItem ? 'disabled' : ''}`}
                        onClick={handlePrev}
                        disabled={!prevItem}
                      >
                        <i className="fas fa-arrow-left"></i>
                        <span>{prevItem ? content[prevItem.key]?.title : 'Previous'}</span>
                      </button>
                      <button 
                        className={`docs-footer-btn next ${!nextItem ? 'disabled' : ''}`}
                        onClick={handleNext}
                        disabled={!nextItem}
                      >
                        <span>{nextItem ? content[nextItem.key]?.title : 'Next'}</span>
                        <i className="fas fa-arrow-right"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Docs;



